<template>
  <div class="d-flex justify-center my-4">
    <v-menu v-model="menuOpen" top offset-y :close-on-content-click="false" @input="onMenuToggle">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          ref="statusBtn"
          dark
          depressed
          tile
          class="refund-status-btn"
          :loading="updating"
          :disabled="updating"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon left small>mdi-arrow-up</v-icon>
          {{ $t('refund.changeStatus') }}
          <v-divider vertical dark class="mx-3"></v-divider>
          <v-icon small>mdi-triangle</v-icon>
        </v-btn>
      </template>
      <v-card :min-width="menuWidth">
        <v-subheader class="refund-status-menu-title">{{ $t('refund.chooseNewStatus') }}</v-subheader>
        <v-list dense>
          <v-list-item
            v-for="option in statusOptions"
            :key="option.value"
            :disabled="updating || !isStatusAllowed(option.value)"
            @click="selectStatus(option.value)"
          >
            <v-list-item-content>
              <div class="d-flex align-start">
                <div class="mr-3 refund-status-chip-col">
                  <v-chip small label :color="option.color" :text-color="option.textColor" class="refund-status-chip">
                    {{ option.label }}
                  </v-chip>
                </div>
                <div class="flex-grow-1">
                  <span class="font-weight-medium">{{ option.title }}</span>
                  <v-list-item-subtitle class="refund-status-desc">{{ option.description }}</v-list-item-subtitle>
                </div>
              </div>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
  import {
    getAllowedRefundStatusTransitions,
    getRefundStatusBackgroundColor,
    getRefundStatusTextColor,
  } from '../../helpers/refundHelper'

  export default {
    name: 'RefundStatusMenu',
    props: {
      currentStatus: {
        type: String,
        default: '',
      },
      // Locks the action (button + items) while a status change is being persisted, so a
      // rapid double-click cannot trigger two concurrent PATCH requests.
      updating: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        menuWidth: null,
        menuOpen: false,
      }
    },
    computed: {
      statusOptions() {
        return [
          {
            value: 'pending',
            label: this.$t('refund.statusPending'),
            title: this.$t('refund.statusPendingTitle'),
            description: this.$t('refund.statusPendingDesc'),
            color: getRefundStatusBackgroundColor('pending'),
            textColor: getRefundStatusTextColor('pending'),
          },
          {
            value: 'refunded',
            label: this.$t('refund.statusRefunded'),
            title: this.$t('refund.statusRefundedTitle'),
            description: this.$t('refund.statusRefundedDesc'),
            color: getRefundStatusBackgroundColor('refunded'),
            textColor: getRefundStatusTextColor('refunded'),
          },
          {
            value: 'failed',
            label: this.$t('refund.statusFailed'),
            title: this.$t('refund.statusFailedTitle'),
            description: this.$t('refund.statusFailedDesc'),
            color: getRefundStatusBackgroundColor('failed'),
            textColor: getRefundStatusTextColor('failed'),
          },
        ]
      },
    },
    methods: {
      // Whether a status is a valid transition from the current one. Disallowed options
      // (including the current status itself) stay listed but disabled/greyed out.
      isStatusAllowed(status) {
        return getAllowedRefundStatusTransitions(this.currentStatus).includes(status)
      },
      // Match the dropdown card width to the activator button so the menu lines up with it.
      onMenuToggle(open) {
        if (open && this.$refs.statusBtn) {
          this.menuWidth = this.$refs.statusBtn.$el.offsetWidth
        }
      },
      selectStatus(status) {
        if (this.updating || !this.isStatusAllowed(status)) {
          return
        }
        // Close the menu immediately so a second click cannot re-trigger the change.
        this.menuOpen = false
        this.$emit('changeStatus', status)
      },
    },
  }
</script>

<style scoped>
  .refund-status-btn {
    background-color: #2c2c2c !important;
    letter-spacing: 0.04em;
    /* Never let the button exceed the available width (its FR label is the longest action label
       on the page and would otherwise force the whole page to scroll horizontally on phones). */
    max-width: 100%;
  }
  /* On phones, make it a full-width action button whose label is allowed to wrap onto two lines
     instead of overflowing the viewport. */
  @media (max-width: 599px) {
    .refund-status-btn {
      width: 100%;
      height: auto !important;
      min-height: 36px;
      padding-top: 8px;
      padding-bottom: 8px;
    }
    .refund-status-btn ::v-deep .v-btn__content {
      white-space: normal;
      /* Clamp the flex content to the button width so the label actually wraps instead of
         overflowing the (overflow-hidden) button and pushing the page width out. */
      max-width: 100%;
      flex-wrap: wrap;
    }
  }
  .refund-status-menu-title {
    font-size: 0.75rem;
    letter-spacing: 0.05em;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
  }
  /* Fixed-width chip column so every status title lines up vertically, regardless of the
     varying chip label widths (e.g. "IN PROGRESS" vs "FAILED"). */
  .refund-status-chip-col {
    flex: 0 0 auto;
    min-width: 92px;
  }
  .refund-status-chip {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.04em;
  }
  .refund-status-desc {
    margin-top: 2px;
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.6);
    /* Let the description wrap on narrow viewports instead of being truncated with an ellipsis
       (Vuetify's v-list-item__subtitle defaults to white-space: nowrap), so the menu stays fully
       readable once it can no longer grow as wide as on desktop. */
    white-space: normal;
  }
  .refund-current-status {
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.5);
  }
  .v-list-item--disabled {
    opacity: 0.5;
  }
</style>
